import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowUpDown, Check, ChevronDown, ChevronUp, CloudOff, Download, Eye, FileImage, FileSpreadsheet, FileText, FileUp,
  FileX, FolderInput, FolderOpen, History, Hourglass, Info, Layers, Link2, SearchX, Trash2, Upload,
} from 'lucide-react';
import api, { openAuthenticatedFile } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import { TONE_TEXT_CLASS, toneClass } from '@/lib/status';
import { getErrorMessage } from '@/lib/errors';
import { canEdit as canEditModule, hasPermission } from '@/lib/permissions';
import { useUrlState } from '@/hooks/useUrlState';
import { useFeature } from '@/hooks/useIntegrationStatus';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { StatusPill } from '@/components/ui/status-pill';
import { RowActions } from '@/components/ui/row-actions';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterChip } from '@/components/ui/filter-chip';
import { ChipSelect } from '@/components/ui/chip-select';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ATTENTION_FILTERS, AWAITING_TAB, DOCUMENT_LIST_LIMIT, LPO_LOI_STAGE, REVIEW_STATUSES, SORTS, TYPE_OPTIONS, TYPE_STAGE,
  TYPE_TABS, UNLINKED, applyColumnFilters, countByTab, documentStage, documentTypeLabel, documentsToCsv, fileExtension,
  fileKind, filterDocuments, filterOptions, hasFile, isAwaitingResponse, isLatest, isPreviewable, isUnlinked, mergeDocuments, plural,
  projectSubLine, reviewLabel, reviewStatusOf, summarizeDocuments, typeTabKey, groupVersions, versionGroupKey,
} from '@/components/documents-ai/documentsModel';
import {
  Avatar, DOCUMENT_ACCEPT, IconTile, SearchField, Segmented, downloadAuthenticatedFile, edgeFadeStyle, pickFile, saveText,
  useDebouncedValue, useMediaQuery, useScrollEdges,
} from '@/components/documents-ai/ui';
import UploadVersionDialog from '@/components/documents-ai/UploadVersionDialog';
import UploadDocumentPanel from '@/components/documents-ai/UploadDocumentPanel';
import DocumentVersionsPanel, { CompareVersionsDialog } from '@/components/documents-ai/DocumentVersionsPanel';
import LinkProjectPopover from '@/components/documents-ai/LinkProjectPopover';
import { useProjectList } from '@/components/documents-ai/ProjectPicker';
import ReviewStatusControl, { ReviewStatusPill } from '@/components/documents-ai/ReviewStatusControl';
import CoverageCard from '@/components/documents-ai/CoverageCard';
import { OneDriveSection, SOURCES, SourceChip, useOneDriveIndex } from '@/components/documents-ai/OneDriveFiles';

const FILE_TILES = {
  doc: { icon: FileText, tone: 'navy' },
  sheet: { icon: FileSpreadsheet, tone: 'green' },
  image: { icon: FileImage, tone: 'blue' },
  variation: { icon: FileText, tone: 'violet' },
  none: { icon: FileX, tone: 'amber' },
};

// View state lives in the URL (reload, copied link and Back keep the same view). ?doc=<id> highlights one.
const VIEW = {
  q: '', tab: 'all', versions: 'latest', show: '', sort: 'updated', project: '', stage: '', by: '', doc: '', source: 'all',
};

// The api instance defaults to JSON; uploads must say multipart or axios turns FormData into JSON.
const MULTIPART = { 'Content-Type': 'multipart/form-data' };
const COLUMNS = 7;
const NO_VERSIONS = [];

const thClass = 'sticky top-0 z-[5] h-9 whitespace-nowrap border-b border-line bg-surface-2 px-3 text-left text-[11.5px] font-bold tracking-[0.02em] text-ink-3 first:rounded-tl-card last:rounded-tr-card';
const tdClass = 'border-b border-line-2 px-3 py-2 align-middle';

function FileTile({ doc }) {
  const tile = FILE_TILES[fileKind(doc)] || FILE_TILES.doc;
  const ext = fileExtension(doc);
  return <IconTile icon={tile.icon} tone={tile.tone} size={32} title={hasFile(doc) ? (ext || 'File') : 'No file attached'} />;
}

function ProjectCell({ doc, link }) {
  if (isUnlinked(doc)) {
    return (
      <div className="min-w-0">
        <StatusPill status="Unlinked" size="sm" title="This document's project no longer exists — link it to a project" />
        {link}
      </div>
    );
  }
  const sub = projectSubLine(doc);
  return (
    <div className="min-w-0">
      <Link
        to={`/projects/${doc.project_id}`}
        onClick={(event) => event.stopPropagation()}
        className="block truncate text-[13px] font-semibold text-brand-ink hover:underline"
      >
        {doc.project_name}
      </Link>
      {sub ? <span className="block truncate text-[11.5px] text-ink-3" title={sub}>{sub}</span> : null}
    </div>
  );
}

/** The stage tag: the document's own stage, or (lighter, dashed) the stage its type belongs to. */
function StageTag({ doc, canSeeLpoLoi }) {
  const stage = documentStage(doc, { canSeeLpoLoi });
  if (!stage.name) return <span className="text-ink-4" aria-label="No stage">—</span>;
  return (
    <span
      className={cn(
        'inline-flex h-6 max-w-full items-center gap-1 rounded-md border px-2 text-[12px] font-medium',
        stage.derived ? 'border-dashed border-line-2 bg-surface text-ink-3' : 'border-line bg-surface-2 text-ink-2'
      )}
      title={stage.derived ? `From the document type — not filed under a stage yet` : `Filed under ${stage.name}`}
      data-testid={`doc-stage-${doc.id}`}
    >
      <Layers className="hidden h-3 w-3 shrink-0 2xl:block" aria-hidden="true" />
      <span className="truncate">{stage.name}</span>
    </span>
  );
}

/** "Costing Sheet · Final costing" — the remarks, or the file type when there are none. */
function documentDetail(doc) {
  const remarks = String(doc.remarks || '').trim();
  return remarks || (hasFile(doc) ? fileExtension(doc) : '');
}

/**
 * Sub line: "Type · remarks", then "· No file attached · Attach" when there is no file. What gives way on a
 * narrow column, in order: the remarks first (they grow into the room left, from zero), then the type label;
 * the missing-file note and the Attach button (and its focus ring) always stay whole. Below 1400 px the note
 * is the short "No file", as on the phone card. The full "Type · remarks" is in the tooltip.
 */
function DocumentSub({ doc, canEdit, onAttach, query = '' }) {
  const typeLabel = documentTypeLabel(doc.type);
  const detail = documentDetail(doc);
  const full = detail ? `${typeLabel} · ${detail}` : typeLabel;
  const withFile = hasFile(doc);
  // A search that hit the remarks always shows them, so the hit is explained (even on a narrow screen).
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  const searchHit = words.length > 0 && words.some((word) => detail.toLowerCase().includes(word));
  const lead = (
    <>
      <span className="min-w-0 truncate" title={full}>{typeLabel}</span>
      {detail ? (
        // basis 0 + grow up to its own width: it only takes the room the label and the note leave over. Without a
        // file, below 1400 px the note needs that room — the remarks would be a crumb ("· I…"), so they wait for the
        // tooltip and the open row, unless a search found them.
        <span
          className={cn('min-w-0 max-w-max shrink-0 grow basis-0 truncate', !withFile && !searchHit && 'hidden min-[1400px]:block')}
          title={full}
          data-testid={`doc-sub-detail-${doc.id}`}
        >
          <span className="whitespace-pre">{' · '}</span>{detail}
        </span>
      ) : null}
    </>
  );
  if (withFile) {
    return <span className="mt-px flex min-w-0 items-baseline text-[12px] text-ink-3">{lead}</span>;
  }
  return (
    <span className="mt-px flex min-w-0 items-baseline text-[12px] text-ink-3">
      {lead}
      <span className="shrink-0 whitespace-pre">
        {' · '}
        <span className="font-semibold text-status-amber-ink min-[1400px]:hidden">No file</span>
        <span className="hidden font-semibold text-status-amber-ink min-[1400px]:inline">No file attached</span>
        {canEdit ? (
          <>
            {' · '}
            <button
              type="button"
              onClick={(event) => { event.stopPropagation(); onAttach(doc); }}
              className="rounded-[3px] font-semibold text-brand-ink underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              data-testid={`attach-document-${doc.id}`}
            >
              Attach
            </button>
          </>
        ) : null}
      </span>
    </span>
  );
}

function SkeletonRows({ count = 6 }) {
  return Array.from({ length: count }).map((_, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <tr key={index} aria-hidden="true">
      <td className={tdClass} colSpan={COLUMNS}>
        <div className="flex items-center gap-3 py-1">
          <span className="ui-skeleton-shimmer h-8 w-8 rounded-lg bg-surface-2" />
          <span className="flex-1 space-y-1.5">
            <span className="ui-skeleton-shimmer block h-3 w-1/3 rounded bg-surface-2" />
            <span className="ui-skeleton-shimmer block h-2.5 w-1/5 rounded bg-surface-2" />
          </span>
        </div>
      </td>
    </tr>
  ));
}

const hasDraggedFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

export default function DocumentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [view, setView] = useUrlState(VIEW);
  const [documents, setDocuments] = useState([]);
  const [loadState, setLoadState] = useState('loading');
  const [loadError, setLoadError] = useState('');
  const debouncedQuery = useDebouncedValue(view.q, 250);
  const tab = view.tab || 'all';
  const versions = view.versions === 'all' ? 'all' : 'latest';
  const attention = ATTENTION_FILTERS[view.show] ? view.show : '';
  const sort = SORTS[view.sort] ? view.sort : 'updated';
  const [versionTarget, setVersionTarget] = useState(null);
  const [versionFile, setVersionFile] = useState(null);
  const [expandedId, setExpandedId] = useState('');
  const [upload, setUpload] = useState({ open: false, initial: null, dropped: null });
  const [compare, setCompare] = useState(null);
  const [dragDepth, setDragDepth] = useState(0);
  const [serverMatches, setServerMatches] = useState([]);
  const [mobileLinkId, setMobileLinkId] = useState('');
  const searchRef = useRef(null);
  const rowRefs = useRef({});
  const tabRefs = useRef({});
  const tabListRef = useRef(null);
  const appliedHighlightRef = useRef('');
  const droppedSeqRef = useRef(0);
  const highlightId = view.doc || '';
  // Edit level on Files (Admin › Access View / Edit; Viewers are read-only) — the server refuses the same writes.
  const canEdit = canEditModule(user, 'files');
  const canSeeLpoLoi = hasPermission(user, 'stage_lpo_loi');
  // The project list feeds the Upload drawer, "Link to project" and the Coverage card (16 idea 9), so it loads with the page.
  const projectList = useProjectList('/documents/projects', true);
  const onedrive = useFeature('onedrive');
  const source = SOURCES[view.source] ? view.source : 'all';
  const showOneDrive = onedrive.enabled && source !== 'uploaded';
  const showUploaded = !onedrive.enabled || source !== 'onedrive';
  const oneDriveIndex = useOneDriveIndex({
    enabled: showOneDrive,
    query: debouncedQuery.trim(),
    projectId: view.project && view.project !== UNLINKED ? view.project : '',
  });
  // Kept for the drawer / link popover API; the list is already loading with the page.
  const wantProjects = useCallback(() => {}, []);
  const modalOpen = upload.open || Boolean(versionTarget) || Boolean(compare);

  // The "Drop to upload" overlay ends with every drop or cancelled drag, wherever it happened — also a drop
  // that a nested drop zone handled and stopped before it reached the page.
  useEffect(() => {
    const endDrag = () => setDragDepth(0);
    window.addEventListener('drop', endDrag, true);
    window.addEventListener('dragend', endDrag, true);
    return () => {
      window.removeEventListener('drop', endDrag, true);
      window.removeEventListener('dragend', endDrag, true);
    };
  }, []);
  useEffect(() => { if (modalOpen) setDragDepth(0); }, [modalOpen]);

  const loadDocuments = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoadState('loading');
    try {
      const response = await api.get('/documents');
      setDocuments(Array.isArray(response.data) ? response.data : []);
      setLoadState('ready');
      setLoadError('');
    } catch (err) {
      setLoadError(getErrorMessage(err, 'Could not load documents'));
      setLoadState('error');
    }
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  // The API returns the newest 500 only. When the list is cut off, the page says so, and a search or a
  // project filter also asks the server (?search=, ?project_id=) so older documents are still found.
  const capped = documents.length >= DOCUMENT_LIST_LIMIT;
  const serverQuery = capped ? debouncedQuery.trim().slice(0, 100) : '';
  const serverProject = capped && view.project && view.project !== UNLINKED ? view.project : '';
  useEffect(() => {
    if (!serverQuery && !serverProject) {
      setServerMatches([]);
      return undefined;
    }
    let cancelled = false;
    const params = {};
    if (serverQuery) params.search = serverQuery;
    if (serverProject) params.project_id = serverProject;
    api.get('/documents', { params })
      .then((response) => { if (!cancelled) setServerMatches(Array.isArray(response.data) ? response.data : []); })
      .catch(() => { if (!cancelled) setServerMatches([]); });
    return () => { cancelled = true; };
  }, [serverQuery, serverProject, documents]);
  const pool = useMemo(() => mergeDocuments(documents, serverMatches), [documents, serverMatches]);
  const olderMatches = pool.length - documents.length;
  // Versions of every document, grouped once per list (a row looks its own up instead of scanning the list).
  const versionGroups = useMemo(() => groupVersions(pool), [pool]);
  const versionsOfDoc = (doc) => (doc ? versionGroups.get(versionGroupKey(doc)) || NO_VERSIONS : NO_VERSIONS);

  const summary = useMemo(() => summarizeDocuments(documents), [documents]);

  // Tab counts and chip options follow every other filter, so "Drawings 3" is what that tab will show.
  const searched = useMemo(
    () => filterDocuments(pool, { tab: 'all', versions, attention, query: debouncedQuery, sort }),
    [pool, versions, attention, debouncedQuery, sort]
  );
  const columnFilters = { project: view.project, stage: view.stage, uploader: view.by, canSeeLpoLoi };
  const baseRows = useMemo(
    () => applyColumnFilters(searched, columnFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searched, view.project, view.stage, view.by, canSeeLpoLoi]
  );
  const tabCounts = useMemo(() => countByTab(baseRows), [baseRows]);
  const inTabFilter = useCallback((doc) => (
    tab === 'all' || (tab === AWAITING_TAB ? isAwaitingResponse(doc) : typeTabKey(doc.type) === tab)
  ), [tab]);
  const rows = useMemo(() => baseRows.filter(inTabFilter), [baseRows, inTabFilter]);
  const chipOptions = useMemo(() => {
    const inTab = (list) => list.filter(inTabFilter);
    return {
      projects: filterOptions(inTab(applyColumnFilters(searched, { ...columnFilters, project: '' })), { canSeeLpoLoi }).projects,
      stages: filterOptions(inTab(applyColumnFilters(searched, { ...columnFilters, stage: '' })), { canSeeLpoLoi }).stages,
      uploaders: filterOptions(inTab(applyColumnFilters(searched, { ...columnFilters, uploader: '' })), { canSeeLpoLoi }).uploaders,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, inTabFilter, view.project, view.stage, view.by, canSeeLpoLoi]);
  const visibleTabs = TYPE_TABS.filter((item) => tabCounts[item.key] > 0 || item.key === tab);
  // "Awaiting response" (16 idea 8): Submitted or Revise & resubmit, across types — after a divider, when anything waits.
  const awaitingTab = tabCounts[AWAITING_TAB] > 0 || tab === AWAITING_TAB
    ? [{ key: AWAITING_TAB, label: 'Awaiting response', icon: Hourglass, divider: true, tone: 'amber' }]
    : [];
  const tabItems = [{ key: 'all', label: 'All' }, ...visibleTabs, ...awaitingTab];
  // On a narrow screen the tab row scrolls sideways: a fade marks the hidden side, and the chosen tab is
  // scrolled into view (sideways only — the page itself does not move).
  const tabEdges = useScrollEdges(tabListRef, { watch: tabItems.map((item) => item.key).join('|') });
  useEffect(() => {
    const list = tabListRef.current;
    const node = tabRefs.current[tab];
    if (!list || !node || list.scrollWidth <= list.clientWidth) return;
    const box = list.getBoundingClientRect();
    const item = node.getBoundingClientRect();
    if (item.left < box.left) list.scrollLeft -= box.left - item.left + 28;
    else if (item.right > box.right) list.scrollLeft += item.right - box.right + 28;
  }, [tab, tabEdges.end]);
  const rowsWithFile = rows.filter(hasFile).length;
  const columnFiltered = Boolean(view.project || view.stage || view.by);
  const filtersActive = Boolean(debouncedQuery || attention || tab !== 'all' || columnFiltered);

  // A copied link (/documents?doc=<id>) scrolls to and highlights that document — once per link, so a
  // reload after attach / upload / delete keeps the person's own "Latest only" choice.
  useEffect(() => {
    if (!highlightId || loadState !== 'ready' || appliedHighlightRef.current === highlightId) return undefined;
    appliedHighlightRef.current = highlightId;
    const target = documents.find((doc) => doc.id === highlightId);
    if (!target) {
      notify.info('That document is not in the list', {
        description: documents.length >= DOCUMENT_LIST_LIMIT
          ? `Only the newest ${DOCUMENT_LIST_LIMIT} documents are listed — search for it by title.`
          : 'It may have been deleted.',
      });
      return undefined;
    }
    if (!isLatest(target)) setView({ versions: 'all' });
    const timer = window.setTimeout(() => {
      rowRefs.current[highlightId]?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [highlightId, loadState, documents, setView]);

  const clearFilters = () => setView({ q: '', show: '', tab: 'all', project: '', stage: '', by: '' });

  // The header numbers count latest versions, so every link except "older versions" shows latest only —
  // "5 without a file" then lists exactly 5 rows.
  const showAttention = (key) => {
    const turningOff = attention === key;
    setView({
      show: turningOff ? '' : key,
      tab: 'all',
      ...(turningOff ? {} : { versions: key === 'older' ? 'all' : 'latest' }),
    });
  };

  // Type tabs: one tab stop, arrow keys / Home / End move between tabs (WAI-ARIA tabs pattern).
  const onTabKeyDown = (event, index) => {
    const last = tabItems.length - 1;
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const nextKey = tabItems[next].key;
    setView({ tab: nextKey });
    tabRefs.current[nextKey]?.focus();
  };

  /* ---------------------------------------------------------------- actions */

  const openDocument = async (doc) => {
    if (!hasFile(doc)) return;
    try {
      await openAuthenticatedFile(doc.file_url);
    } catch (err) {
      notify.error(`Could not open “${doc.title}”`, { description: getErrorMessage(err) });
    }
  };

  const downloadDocument = async (doc) => {
    if (!hasFile(doc)) return;
    const ext = fileExtension(doc).toLowerCase();
    try {
      await downloadAuthenticatedFile(doc.file_url, doc.file_name || `${doc.title}${ext ? `.${ext}` : ''}`);
    } catch (err) {
      notify.error(`Could not download “${doc.title}”`, { description: getErrorMessage(err) });
    }
  };

  const undoChange = (undoId) => (undoId ? async () => {
    await api.post(`/undo/entries/${encodeURIComponent(undoId)}`);
    await loadDocuments({ quiet: true });
  } : undefined);

  const attachFile = async (doc) => {
    if (!canEdit) return;
    const file = await pickFile({ accept: DOCUMENT_ACCEPT });
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const response = await api.post(`/documents/${doc.id}/file`, form, { headers: MULTIPART });
      notify.success(`File attached to “${doc.title}”`, { description: file.name, undo: undoChange(response.data?.undo_id) });
      await loadDocuments({ quiet: true });
    } catch (err) {
      notify.error(`Could not attach the file to “${doc.title}”`, { description: getErrorMessage(err) });
    }
  };

  const nextVersionOf = (doc) => {
    if (!doc) return 1;
    const inGroup = versionsOfDoc(doc).map((item) => Number(item.version) || 1);
    return Math.max(Number(doc.version) || 1, ...inGroup) + 1;
  };

  const openVersionUpload = (doc, file = null) => {
    setVersionFile(file);
    setVersionTarget(doc);
  };

  const uploadVersion = async ({ file, remarks }) => {
    const doc = versionTarget;
    const form = new FormData();
    // No stage_id: the server keeps the stage of the document for its new version (also a stage this
    // person may not see, or one an older row still carries that is no longer part of its project).
    form.append('project_id', doc.project_id || '');
    form.append('title', doc.title);
    form.append('doc_type', doc.type);
    form.append('remarks', remarks);
    form.append('file', file);
    const response = await api.post('/documents', form, { headers: MULTIPART });
    const saved = response.data || {};
    notify.success(`“${doc.title}” v${saved.version || nextVersionOf(doc)} uploaded`, {
      description: `v${doc.version || 1} is kept as an older version`,
      undo: undoChange(saved.undo_id),
    });
    await loadDocuments({ quiet: true });
  };

  const openUpload = (initial = null) => {
    wantProjects();
    setUpload({ open: true, initial, dropped: null });
  };

  const onUploaded = async (saved, { replacedVersion }) => {
    notify.success(`“${saved.title}” v${saved.version || 1} uploaded`, {
      description: replacedVersion
        ? `v${replacedVersion} is kept as an older version`
        : [saved.project_name, saved.stage_name].filter(Boolean).join(' · ') || undefined,
      undo: undoChange(saved.undo_id),
    });
    await loadDocuments({ quiet: true });
    if (saved.id) {
      setView({ doc: saved.id, tab: 'all', show: '', q: '', project: '', stage: '', by: '' });
    }
  };

  const linkDocument = async (doc, project) => {
    const wanted = TYPE_STAGE[doc.type];
    const stage = (project.stages || []).find((item) => item.name === wanted && (canSeeLpoLoi || item.name !== LPO_LOI_STAGE));
    const response = await api.put(`/documents/${doc.id}`, { project_id: project.id, stage_id: stage?.id || '' });
    const moved = Number(response.data?.versions_updated) || 1;
    notify.success(`“${doc.title}” linked to ${project.name}`, {
      description: [moved > 1 ? `All ${moved} versions moved` : null, stage ? `Stage: ${stage.name}` : null].filter(Boolean).join(' · ') || undefined,
      undo: undoChange(response.data?.undo_id),
    });
    await loadDocuments({ quiet: true });
  };

  const copyLink = async (doc) => {
    const url = `${window.location.origin}/documents?doc=${encodeURIComponent(doc.id)}`;
    try {
      await navigator.clipboard.writeText(url);
      notify.info('Link copied', { description: `Opens “${doc.title}” in Documents` });
    } catch {
      notify.error('Could not copy the link', { description: url });
    }
  };

  const deleteDocument = async (doc) => {
    const olderVersions = versionsOfDoc(doc).filter((item) => item.id !== doc.id);
    const newestOlder = olderVersions[0];
    let result = null;
    const ok = await confirm({
      title: `Delete “${doc.title}” v${doc.version || 1}?`,
      subtitle: [documentTypeLabel(doc.type), doc.project_name || 'Unlinked', `uploaded ${formatDate(doc.uploaded_at)} by ${doc.uploaded_by || 'unknown'}`].join(' · '),
      description: isLatest(doc) && newestOlder
        ? `v${newestOlder.version} becomes the latest version again.`
        : hasFile(doc) ? 'The document and its file are removed from the register.' : 'The document is removed from the register.',
      note: 'You can undo this right after.',
      tone: 'danger',
      icon: Trash2,
      confirmLabel: 'Delete document',
      onConfirm: async () => {
        const response = await api.delete(`/documents/${doc.id}`);
        result = response.data || {};
      },
    });
    if (!ok) return;
    notify.success(`“${doc.title}” v${doc.version || 1} deleted`, { undo: undoChange(result?.undo_id) });
    await loadDocuments({ quiet: true });
  };

  const onReviewSaved = async (saved) => {
    const status = reviewStatusOf(saved);
    notify.success(`“${saved.title}” v${saved.version || 1} — ${reviewLabel(status)}`, {
      description: REVIEW_STATUSES[status]?.party && saved.review_party ? `${saved.review_party === 'client' ? 'Client' : 'Consultant'} · ${formatDate(saved.review_date)}` : undefined,
      undo: undoChange(saved.undo_id),
    });
    await loadDocuments({ quiet: true });
  };

  // Coverage card: a count shows those documents; Due / Missing opens Upload with the project and stage filled in.
  const showCoverageDocuments = (projectId, stageName) => {
    setView({ project: projectId, stage: stageName, tab: 'all', show: '', q: '', by: '' });
    window.requestAnimationFrame(() => document.getElementById('documents-register')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));
  };
  const uploadForStage = (project, stageName) => {
    const stage = (project.stages || []).find((item) => String(item.name).trim().toLowerCase() === stageName.trim().toLowerCase());
    const type = TYPE_OPTIONS.find((option) => option.stage === stageName)?.value || '';
    openUpload({ projectId: project.id, stageId: stage?.id || '', type });
  };

  // "Index now" starts the index on the server and answers at once; the list polls while it runs, and the run
  // started here is reported when it is done.
  const indexWatchRef = useRef('');
  const indexOneDriveNow = async () => {
    try {
      const response = await api.post('/documents/onedrive-index');
      const data = response.data || {};
      if (data.status === 'started' || data.status === 'running') {
        indexWatchRef.current = data.started_at || new Date().toISOString();
        notify.info(data.status === 'running' ? 'The OneDrive index is already being refreshed' : 'Indexing the project OneDrive folders…', {
          description: 'This can take a few minutes — the list updates when it is done.',
        });
      } else if (data.status === 'succeeded') {
        notify.success('OneDrive index refreshed', { description: data.summary?.files !== undefined ? plural(data.summary.files, 'file') : undefined });
      } else {
        notify.warning('OneDrive index not refreshed', { description: data.reason || data.error || undefined });
      }
      oneDriveIndex.reload();
    } catch (err) {
      notify.error('Could not refresh the OneDrive index', { description: getErrorMessage(err) });
    }
  };
  const { indexing: oneDriveIndexing, loading: oneDriveLoading, lastRun: oneDriveLastRun } = oneDriveIndex;
  useEffect(() => {
    const since = indexWatchRef.current;
    if (!since || oneDriveIndexing || oneDriveLoading || !oneDriveLastRun?.finished_at) return;
    if (Date.parse(oneDriveLastRun.finished_at) < Date.parse(since)) return;
    indexWatchRef.current = '';
    if (oneDriveLastRun.status === 'succeeded') notify.success('OneDrive index refreshed', { description: plural(oneDriveLastRun.files || 0, 'file') });
    else notify.warning('OneDrive index not refreshed', { description: oneDriveLastRun.reason || 'See the job log' });
  }, [oneDriveIndexing, oneDriveLoading, oneDriveLastRun]);

  const exportCsv = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    saveText(documentsToCsv(rows), `documents-${stamp}.csv`);
  };

  const toggleVersions = (doc) => setExpandedId((current) => (current === doc.id ? '' : doc.id));
  const canLink = (doc) => canEdit && isUnlinked(doc) && doc.can_edit !== false;

  const rowMenuItems = (doc) => [
    isPreviewable(doc) && { label: 'Preview', icon: Eye, onSelect: () => openDocument(doc) },
    hasFile(doc) && { label: 'Download', icon: Download, onSelect: () => downloadDocument(doc) },
    canEdit && !hasFile(doc) && { label: 'Attach file…', icon: FileUp, onSelect: () => attachFile(doc), testId: 'doc-menu-attach' },
    canEdit && hasFile(doc) && { label: 'Upload new version', icon: FileUp, onSelect: () => openVersionUpload(doc), testId: 'doc-menu-upload-version' },
    // On a phone the card has no room for the "Link to project" link under the Unlinked pill: it is here.
    isMobile && canLink(doc) && {
      label: 'Link to project…',
      icon: FolderInput,
      onSelect: () => { wantProjects(); setMobileLinkId(doc.id); },
      testId: 'doc-menu-link',
    },
    { label: expandedId === doc.id ? 'Hide version history' : 'Version history', icon: History, onSelect: () => toggleVersions(doc), testId: 'doc-menu-versions' },
    { label: 'Copy link', icon: Link2, onSelect: () => copyLink(doc), testId: 'doc-menu-copy-link' },
    {
      label: 'Open project files',
      icon: FolderOpen,
      onSelect: () => navigate(`/projects/${doc.project_id}?tab=files`),
      disabled: isUnlinked(doc),
      hint: isUnlinked(doc) ? 'Unlinked' : undefined,
    },
    canEdit && {
      label: 'Delete…',
      icon: Trash2,
      danger: true,
      onSelect: () => deleteDocument(doc),
      disabled: !doc.can_delete,
      hint: doc.can_delete ? undefined : 'Admins & uploader',
      testId: 'doc-menu-delete',
    },
  ];

  // Click = open: PDFs and images in a new tab, Excel / Word files download (the browser can't show them).
  const activateRow = (doc) => {
    if (isPreviewable(doc)) openDocument(doc);
    else if (hasFile(doc)) downloadDocument(doc);
    else if (canEdit) attachFile(doc);
  };

  const onRowKeyDown = (event, doc) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activateRow(doc);
    }
  };

  // Drop a file anywhere on the page → the Upload drawer opens with it (title / type / project suggested).
  // Drop zones of their own (a row's "Drop a file to save as v3", the Upload new version dialog, the
  // drawer's file box) handle their drops and stop them, so they never reach this. Drag events from the
  // open drawer or a dialog (React portals) still bubble here: they never count for the page overlay,
  // and a file dropped on an open drawer goes into that drawer — keeping what was typed — instead of
  // opening a second one.
  const onPage = (event) => event.currentTarget.contains(event.target);
  const pageDrop = canEdit ? {
    onDragEnter: (event) => {
      if (!hasDraggedFiles(event)) return;
      event.preventDefault();
      if (!modalOpen && onPage(event)) setDragDepth((depth) => depth + 1);
    },
    onDragOver: (event) => {
      if (!hasDraggedFiles(event)) return;
      event.preventDefault(); // the browser must not open a file dropped here in place of the app
      if (modalOpen && !upload.open && event.dataTransfer) event.dataTransfer.dropEffect = 'none';
    },
    onDragLeave: (event) => {
      if (hasDraggedFiles(event) && !modalOpen && onPage(event)) setDragDepth((depth) => Math.max(0, depth - 1));
    },
    onDrop: (event) => {
      if (!hasDraggedFiles(event)) return;
      event.preventDefault();
      setDragDepth(0);
      const file = event.dataTransfer.files?.[0];
      if (!file) return;
      if (upload.open) {
        droppedSeqRef.current += 1;
        setUpload((current) => ({ ...current, dropped: { file, seq: droppedSeqRef.current } }));
      } else if (!modalOpen) {
        openUpload({ file });
      }
    },
  } : {};

  /* ---------------------------------------------------------------- render */

  const alerts = loadState === 'ready' ? [
    summary.withoutFile > 0 && {
      key: 'no_file',
      label: `${summary.withoutFile} without a file`,
      tone: 'amber',
      onClick: () => showAttention('no_file'),
    },
    summary.unlinked > 0 && {
      key: 'unlinked',
      label: `${summary.unlinked} not linked to a project`,
      tone: 'amber',
      onClick: () => showAttention('unlinked'),
    },
    summary.older > 0 && {
      key: 'older',
      label: `${summary.older} older ${summary.older === 1 ? 'version' : 'versions'} kept`,
      tone: 'gray',
      onClick: () => showAttention('older'),
    },
  ] : [];

  const subtitle = loadState === 'ready'
    ? [
      isMobile ? null : 'Project document register',
      plural(summary.total, 'document'),
      summary.linkedProjects ? plural(summary.linkedProjects, 'project') : null,
      capped ? `newest ${DOCUMENT_LIST_LIMIT} loaded` : null,
    ].filter(Boolean).join(' · ')
    : 'Project document register';

  const renderActions = (doc) => (
    <div className="flex items-center justify-end gap-0.5">
      {hasFile(doc) ? (
        <>
          {isPreviewable(doc) ? (
            <Button type="button" variant="ghost" size="icon-xs" aria-label={`Preview ${doc.title}`} title="Preview" className="text-ink-3"
              onClick={(event) => { event.stopPropagation(); openDocument(doc); }}>
              <Eye aria-hidden="true" />
            </Button>
          ) : null}
          <Button type="button" variant="ghost" size="icon-xs" aria-label={`Download ${doc.title}`} title="Download" className="text-ink-3"
            onClick={(event) => { event.stopPropagation(); downloadDocument(doc); }}>
            <Download aria-hidden="true" />
          </Button>
        </>
      ) : canEdit ? (
        <Button type="button" variant="ghost" size="icon-xs" aria-label={`Attach a file to ${doc.title}`} title="Attach file" className="text-ink-3"
          onClick={(event) => { event.stopPropagation(); attachFile(doc); }}>
          <FileUp aria-hidden="true" />
        </Button>
      ) : null}
      <RowActions label={`Actions for ${doc.title}`} items={rowMenuItems(doc)} data-testid={`document-actions-${doc.id}`} />
    </div>
  );

  const linkControl = (doc) => (canEdit && doc.can_edit !== false ? (
    <LinkProjectPopover
      doc={doc}
      versionCount={versionsOfDoc(doc).length}
      projectList={projectList}
      onOpen={wantProjects}
      onLink={linkDocument}
    />
  ) : null);

  const versionsPanel = (doc) => (
    <DocumentVersionsPanel
      doc={doc}
      versions={versionsOfDoc(doc)}
      canEdit={canEdit}
      onOpen={openDocument}
      onDownload={downloadDocument}
      onAttach={attachFile}
      onUploadVersion={(latest, file) => openVersionUpload(latest, file)}
      onCompare={(older, newer) => setCompare({ older, newer })}
      onReviewSaved={onReviewSaved}
    />
  );

  const versionCell = (doc) => {
    const groupSize = versionsOfDoc(doc).length;
    const older = Math.max(0, groupSize - 1);
    const expanded = expandedId === doc.id;
    const Chevron = expanded ? ChevronUp : ChevronDown;
    const latest = isLatest(doc);
    // The count line only on the latest version ("v2" over "1 older"); an older version says Superseded instead.
    const countLine = latest && older;
    const chevron = (
      <Chevron
        className={cn('h-3 w-3 shrink-0', countLine || expanded ? 'text-brand-ink' : 'text-ink-4 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible/version:opacity-100')}
        aria-hidden="true"
      />
    );
    return (
      <div className="flex min-w-0 flex-col items-start gap-0.5 whitespace-nowrap">
        <button
          type="button"
          onClick={(event) => { event.stopPropagation(); toggleVersions(doc); }}
          aria-expanded={expanded}
          aria-controls={expanded ? `doc-versions-row-${doc.id}` : undefined}
          aria-label={`${expanded ? 'Hide' : 'Show'} the version history of ${doc.title} (v${doc.version || 1}${older ? `, ${older} ${latest ? 'older' : 'other'}` : ''})`}
          title={older ? 'Version history' : 'Version history · upload a new version'}
          data-testid={`doc-versions-toggle-${doc.id}`}
          className={cn(
            'group/version -mx-1 inline-flex max-w-[calc(100%+0.5rem)] flex-col items-start rounded-md px-1 py-0.5 text-left leading-tight hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            expanded && 'bg-surface'
          )}
        >
          <span className="inline-flex items-center gap-1">
            <span className="font-mono text-[12.5px] font-semibold text-ink-2">v{doc.version || 1}</span>
            {countLine ? null : chevron}
          </span>
          {countLine ? (
            <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-ink">
              {older} older
              {chevron}
            </span>
          ) : null}
        </button>
        {!latest ? (
          // plain text, not a pill: it fits the narrow column (the version panel shows the Superseded pill)
          <span className="text-[11.5px] font-semibold text-ink-3" title="An older version — the newest one is listed too">Superseded</span>
        ) : null}
      </div>
    );
  };

  const emptyBody = documents.length === 0 ? (
    <EmptyState
      icon={FileText}
      title="No documents yet"
      text="Drawings, submittals, BOQs and LPOs filed on projects appear here with all their versions."
      action={canEdit ? <Button size="sm" onClick={() => openUpload()}><Upload aria-hidden="true" />Upload document</Button> : null}
    />
  ) : (
    <EmptyState
      icon={SearchX}
      title="No documents match"
      text={debouncedQuery ? `Nothing found for “${debouncedQuery}”. Search covers title, remarks, project, stage and uploader.` : 'Try another type or remove a filter.'}
      action={<Button variant="outline" size="sm" onClick={clearFilters}>Clear filters</Button>}
    />
  );

  return (
    <div data-testid="documents-page" className="relative space-y-5" {...pageDrop}>
      <PageHeader
        title="Documents"
        count={loadState === 'ready' ? summary.total : undefined}
        subtitle={subtitle}
        alerts={isMobile ? undefined : alerts}
        className="max-sm:flex-col max-sm:items-stretch"
        actions={(
          <>
            <Button type="button" variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="export-documents-csv">
              <Download aria-hidden="true" />
              Export CSV
            </Button>
            {canEdit ? (
              <Button type="button" onClick={() => openUpload()} data-testid="upload-document">
                <Upload aria-hidden="true" />
                Upload document
              </Button>
            ) : null}
          </>
        )}
      >
        {isMobile && alerts.some(Boolean) ? (
          // On a phone the attention links wrap; each has its own dot, so no " · " is left dangling at a line end.
          <div className="mt-1.5 flex flex-wrap gap-x-3.5 gap-y-1" data-testid="documents-attention">
            {alerts.filter(Boolean).map((alert) => (
              <button
                key={alert.key}
                type="button"
                onClick={alert.onClick}
                className={cn(
                  'inline-flex items-center gap-[5px] whitespace-nowrap rounded text-[12.5px] font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  TONE_TEXT_CLASS[alert.tone] || TONE_TEXT_CLASS.amber
                )}
              >
                <span className={cn('ui-tone-dot h-[7px] w-[7px]', toneClass(alert.tone))} aria-hidden="true" />
                {alert.label}
              </button>
            ))}
          </div>
        ) : null}
      </PageHeader>

      {/* Type tabs + version mode */}
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 border-b border-line">
        <div
          ref={tabListRef}
          role="tablist"
          aria-label="Document type"
          data-testid="type-filter"
          data-overflow={tabEdges.start || tabEdges.end ? 'true' : undefined}
          className="-mb-px flex min-w-0 max-w-full gap-[14px] overflow-x-auto"
          style={edgeFadeStyle(tabEdges)}
        >
          {tabItems.map((item, index) => {
            const active = tab === item.key;
            const TabIcon = item.icon;
            return [
              item.divider ? <span key={`${item.key}-divider`} className="mb-[11px] mt-2 w-px shrink-0 self-stretch bg-line-2" aria-hidden="true" /> : null,
              <button
                key={item.key}
                ref={(node) => { tabRefs.current[item.key] = node; }}
                type="button"
                role="tab"
                id={`doc-tab-${item.key}`}
                aria-selected={active}
                aria-controls="documents-register"
                tabIndex={active ? 0 : -1}
                data-testid={`doc-tab-${item.key}`}
                onClick={() => setView({ tab: item.key })}
                onKeyDown={(event) => onTabKeyDown(event, index)}
                className={cn(
                  'relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-t-md px-1 pb-[11px] pt-2 text-[13.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  'after:absolute after:inset-x-1 after:bottom-0 after:h-[2.5px] after:rounded-t',
                  active ? 'text-ink after:bg-brand' : 'text-ink-3 after:bg-transparent hover:text-ink-2'
                )}
              >
                {TabIcon ? <TabIcon className={cn('h-3.5 w-3.5', active ? 'text-ink' : 'text-status-amber-ink')} aria-hidden="true" /> : null}
                {item.label}
                <span className={cn(
                  'rounded-full px-[7px] py-px text-[11px] font-bold tabular-nums',
                  active ? 'bg-brand text-brand-fg'
                    : item.tone === 'amber' ? 'bg-status-amber-bg text-status-amber-ink' : 'bg-[color:var(--status-gray-bg)] text-ink-2'
                )}>
                  {tabCounts[item.key] || 0}
                </span>
              </button>,
            ];
          })}
        </div>
        <Segmented
          className="mb-2"
          ariaLabel="Versions"
          value={versions}
          onChange={(next) => setView({ versions: next, ...(next === 'latest' && attention === 'older' ? { show: '' } : {}) })}
          options={[
            { key: 'latest', label: 'Latest only', testId: 'doc-versions-latest' },
            { key: 'all', label: 'All versions', testId: 'doc-versions-all' },
          ]}
        />
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchField
          ref={searchRef}
          value={view.q}
          onChange={(next) => setView({ q: next })}
          label="Search documents"
          placeholder="Search title, project, uploader…"
          className="w-full sm:w-[320px] 2xl:w-[340px]"
          inputClassName="text-ellipsis"
          shortcut={!isMobile}
          testId="search-documents"
        />
        {attention ? (
          <FilterChip
            label="Showing"
            value={ATTENTION_FILTERS[attention]?.label}
            onClear={() => setView({ show: '' })}
            data-testid="doc-attention-chip"
          />
        ) : null}
        <div className="flex flex-wrap items-center gap-2" data-testid="documents-filter-chips">
          <ChipSelect
            label="Project"
            value={view.project || null}
            options={chipOptions.projects}
            onChange={(next) => setView({ project: next || '' })}
            allLabel="All projects"
            emptyText="No projects in this view"
            popoverClassName="w-[280px]"
            testId="doc-filter-project"
          />
          <ChipSelect
            label="Stage"
            value={view.stage || null}
            options={chipOptions.stages}
            onChange={(next) => setView({ stage: next || '' })}
            allLabel="All stages"
            emptyText="No stages in this view"
            footer="Documents without a stage count under the stage of their type."
            popoverClassName="w-[260px]"
            testId="doc-filter-stage"
          />
          <ChipSelect
            label="Uploaded by"
            value={view.by || null}
            options={chipOptions.uploaders}
            onChange={(next) => setView({ by: next || '' })}
            allLabel="Anyone"
            emptyText="No uploaders in this view"
            popoverClassName="w-[240px]"
            testId="doc-filter-uploader"
          />
          <SourceChip
            value={source}
            onChange={(next) => setView({ source: next })}
            feature={onedrive}
            counts={onedrive.enabled ? { uploaded: rows.length, onedrive: oneDriveIndex.total } : null}
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="whitespace-nowrap text-[12.5px] text-ink-3" data-testid="documents-count">
            {plural(rows.length, 'document')} · {plural(rowsWithFile, 'file')}
          </span>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="text-ink-2" data-testid="documents-sort">
                <ArrowUpDown aria-hidden="true" />
                Sort: {SORTS[sort].label}
                <ChevronDown className="text-ink-3" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              {Object.entries(SORTS).map(([key, option]) => (
                <DropdownMenuItem key={key} onSelect={() => setView({ sort: key })} className="gap-2 text-[13px]">
                  <Check className={cn('h-3.5 w-3.5', key === sort ? 'opacity-100' : 'opacity-0')} aria-hidden="true" />
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Register */}
      <section
        id="documents-register"
        role="tabpanel"
        aria-labelledby={`doc-tab-${tab}`}
        className="min-w-0 rounded-card border border-line bg-surface shadow-card"
      >
        {loadState === 'error' ? (
          <EmptyState
            layout="inline"
            tone="red"
            icon={CloudOff}
            title="Couldn't load documents"
            text={loadError}
            action={<Button size="sm" variant="outline" onClick={() => loadDocuments()}>Try again</Button>}
          />
        ) : !showUploaded ? null : isMobile ? (
          <ul className="divide-y divide-line-2" data-testid="documents-cards">
            {loadState === 'loading' ? (
              <li className="px-4 py-6 text-center text-[13px] text-ink-3">Loading documents…</li>
            ) : rows.length === 0 ? (
              <li>{emptyBody}</li>
            ) : rows.map((doc) => {
              const stage = documentStage(doc, { canSeeLpoLoi });
              const older = Math.max(0, versionsOfDoc(doc).length - 1);
              const expanded = expandedId === doc.id;
              return (
                <li
                  key={doc.id}
                  ref={(node) => { rowRefs.current[doc.id] = node; }}
                  className={cn('px-4 py-3', (highlightId === doc.id || expanded) && 'bg-brand-soft')}
                  data-testid={`document-card-${doc.id}`}
                >
                  <div className="relative flex items-start gap-3">
                    <button type="button" onClick={() => activateRow(doc)} className="-m-1 flex min-w-0 flex-1 items-start gap-3 rounded-lg p-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <FileTile doc={doc} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14px] font-semibold leading-snug text-ink">{doc.title}</span>
                        <span className="mt-0.5 flex min-w-0 items-baseline text-[12px] text-ink-3">
                          <span className="min-w-0 truncate" title={documentDetail(doc) || undefined}>
                            {documentTypeLabel(doc.type)}{documentDetail(doc) ? ` · ${documentDetail(doc)}` : ''}
                          </span>
                          {hasFile(doc) ? null : (
                            <span className="shrink-0 whitespace-pre"> · <span className="font-semibold text-status-amber-ink">No file</span></span>
                          )}
                        </span>
                        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-ink-3">
                          {isUnlinked(doc) ? <StatusPill status="Unlinked" size="sm" /> : <span className="font-semibold text-brand-ink">{doc.project_name}</span>}
                          {stage.name ? <span>{stage.name}</span> : null}
                          <ReviewStatusPill doc={doc} />
                          <span className="font-mono text-ink-2">v{doc.version || 1}</span>
                          {older ? <span>{older} older</span> : null}
                          {!isLatest(doc) ? <StatusPill status="Superseded" size="sm" /> : null}
                          <span>{formatDate(doc.uploaded_at)}</span>
                        </span>
                      </span>
                    </button>
                    <RowActions label={`Actions for ${doc.title}`} items={rowMenuItems(doc)} size="default" data-testid={`document-actions-${doc.id}`} />
                    {mobileLinkId === doc.id && canLink(doc) ? (
                      // "Link to project…" from the card menu: the same project list, in a bottom sheet.
                      <LinkProjectPopover
                        doc={doc}
                        versionCount={versionsOfDoc(doc).length}
                        projectList={projectList}
                        onOpen={wantProjects}
                        onLink={linkDocument}
                        open
                        onOpenChange={(next) => { if (!next) setMobileLinkId(''); }}
                        asSheet
                      />
                    ) : null}
                  </div>
                  {expanded ? (
                    <div className="mt-3 border-t border-line-2 pt-3" id={`doc-versions-row-${doc.id}`}>
                      {versionsPanel(doc)}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="overflow-x-auto">
            {/* Compact columns below 2xl: the register fits a 1280 px laptop (≈ 966 px for the table) with no
                sideways scroll; wider screens give Project / Updated more room. */}
            <table className="w-full min-w-[960px] table-fixed border-separate border-spacing-0 text-[13px] text-ink" data-testid="documents-table">
              <colgroup>
                <col />
                <col className="w-[138px] 2xl:w-[176px]" />
                <col className="w-[144px] 2xl:w-[164px]" />
                <col className="w-[150px] 2xl:w-[164px]" />
                <col className="w-[92px]" />
                <col className="w-[112px] min-[1360px]:w-[140px] 2xl:w-[152px]" />
                <col className="w-[96px]" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className={cn(thClass, 'pl-4')}>Document</th>
                  <th scope="col" className={thClass}>Project</th>
                  <th scope="col" className={cn(thClass, 'px-2 2xl:px-3')}>Stage</th>
                  <th scope="col" className={cn(thClass, 'px-2 2xl:px-3')}>
                    <span className="inline-flex items-center gap-1" title="Where each version stands with the consultant or client: Draft → Submitted → Approved, Approved as noted or Revise & resubmit. Internal documents are For record. Click a status to log a submission or a reply.">
                      Status
                      <Info className="h-3 w-3" aria-hidden="true" />
                    </span>
                  </th>
                  <th scope="col" className={cn(thClass, 'px-2.5 2xl:px-3')}>Version</th>
                  <th scope="col" className={thClass}>Updated</th>
                  <th scope="col" className={thClass}><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loadState === 'loading' ? <SkeletonRows /> : rows.length === 0 ? (
                  <tr><td colSpan={COLUMNS}>{emptyBody}</td></tr>
                ) : rows.map((doc) => {
                  const highlighted = highlightId === doc.id;
                  const expanded = expandedId === doc.id;
                  return [
                    <tr
                      key={doc.id}
                      ref={(node) => { rowRefs.current[doc.id] = node; }}
                      tabIndex={0}
                      onClick={() => activateRow(doc)}
                      onKeyDown={(event) => onRowKeyDown(event, doc)}
                      aria-label={isPreviewable(doc) ? `Open ${doc.title}` : hasFile(doc) ? `Download ${doc.title}` : `${doc.title} — no file attached`}
                      data-testid={`document-row-${doc.id}`}
                      data-expanded={expanded ? 'true' : undefined}
                      className={cn(
                        'group h-[52px] cursor-pointer outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
                        (highlighted || expanded) && 'bg-brand-soft hover:bg-brand-soft [&>td:first-child]:shadow-[inset_3px_0_0_var(--brand-ink)]',
                        expanded && '[&>td]:border-b-transparent',
                        !isLatest(doc) && 'text-ink-2'
                      )}
                    >
                      <td className={cn(tdClass, 'pl-4')}>
                        <div className="flex min-w-0 items-center gap-3">
                          <FileTile doc={doc} />
                          <div className="min-w-0">
                            <span className={cn('block truncate text-[13.5px] font-semibold', isLatest(doc) ? 'text-ink' : 'text-ink-2')} title={doc.title}>{doc.title}</span>
                            <DocumentSub doc={doc} canEdit={canEdit} onAttach={attachFile} query={debouncedQuery} />
                          </div>
                        </div>
                      </td>
                      <td className={tdClass}><ProjectCell doc={doc} link={linkControl(doc)} /></td>
                      <td className={cn(tdClass, 'px-2 2xl:px-3')}><StageTag doc={doc} canSeeLpoLoi={canSeeLpoLoi} /></td>
                      <td className={cn(tdClass, 'px-2 2xl:px-3')}><ReviewStatusControl doc={doc} canEdit={canEdit} onSaved={onReviewSaved} /></td>
                      <td className={cn(tdClass, 'px-2.5 2xl:px-3')}>{versionCell(doc)}</td>
                      <td className={tdClass}>
                        <span className="block text-[13px] text-ink">{formatDate(doc.uploaded_at)}</span>
                        {doc.uploaded_by ? (
                          <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[12px] text-ink-3">
                            <Avatar name={doc.uploaded_by} size={18} />
                            <span className="truncate" title={doc.uploaded_by}>{doc.uploaded_by}</span>
                          </span>
                        ) : null}
                      </td>
                      <td className={cn(tdClass, 'pl-1 pr-1.5')}>
                        <div className="opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 group-focus-visible:opacity-100 has-[[data-state=open]]:opacity-100 [@media(hover:none)]:opacity-100">
                          {renderActions(doc)}
                        </div>
                      </td>
                    </tr>,
                    expanded ? (
                      <tr key={`${doc.id}-versions`} id={`doc-versions-row-${doc.id}`}>
                        <td
                          colSpan={COLUMNS}
                          className="border-b border-line bg-surface-2 py-4 pl-4 pr-5 shadow-[inset_3px_0_0_var(--brand-ink)] lg:pl-16"
                        >
                          {versionsPanel(doc)}
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}
        {showOneDrive && loadState !== 'error' ? (
          <OneDriveSection
            index={oneDriveIndex}
            alone={!showUploaded}
            isAdmin={['Admin', 'Super Admin'].includes(String(user?.role || '').trim())}
            onIndexNow={indexOneDriveNow}
          />
        ) : null}
        {loadState === 'ready' && documents.length ? (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3 text-[12.5px] text-ink-3">
            <span data-testid="documents-foot">
              {showUploaded ? `${rows.length} of ${versions === 'latest' ? pool.filter(isLatest).length : pool.length} ${onedrive.enabled ? 'uploaded documents' : 'documents'}` : 'Uploaded documents hidden'}
              {showOneDrive ? ` · ${Math.min(oneDriveIndex.items.length, oneDriveIndex.total)} of ${plural(oneDriveIndex.total, 'OneDrive file')}` : ''}
              {versions === 'latest' && attention !== 'older' ? ' · latest versions only' : ' · all versions'}
              {filtersActive ? ' · filtered' : ''}
            </span>
            {versions === 'latest' && summary.older > 0 ? (
              <button type="button" onClick={() => setView({ versions: 'all' })} className="font-semibold text-brand-ink hover:underline">
                {summary.older} older {summary.older === 1 ? 'version' : 'versions'} hidden · Show all versions
              </button>
            ) : null}
            {capped ? (
              <span className="w-full font-medium text-status-amber-ink" data-testid="documents-capped">
                Showing the newest {DOCUMENT_LIST_LIMIT} documents; the numbers above cover these only.
                {serverQuery || serverProject
                  ? ` ${serverQuery ? 'Search' : 'The project filter'} also checked older documents${olderMatches ? ` and found ${plural(olderMatches, 'more match', 'more matches')}` : ''}.`
                  : ' Search to find older ones.'}
              </span>
            ) : null}
          </div>
        ) : null}
      </section>

      {loadState !== 'error' ? (
        <CoverageCard
          docs={pool}
          projects={projectList.projects}
          projectsLoading={projectList.loading || loadState === 'loading'}
          projectsError={projectList.error}
          canSeeLpoLoi={canSeeLpoLoi}
          canEdit={canEdit}
          onShowDocuments={showCoverageDocuments}
          onUpload={uploadForStage}
          onShowUnlinked={() => showAttention('unlinked')}
        />
      ) : null}

      {dragDepth > 0 && !modalOpen ? (
        <div className="pointer-events-none fixed inset-0 z-40 grid place-items-center bg-[color:var(--scrim)] p-6" data-testid="documents-drop-overlay">
          <div className="flex flex-col items-center gap-2 rounded-card border-2 border-dashed border-[color:var(--brand-ink)] bg-surface px-10 py-8 text-center shadow-overlay">
            <span className="ui-icon-tile tone-navy h-11 w-11 rounded-xl" aria-hidden="true"><Upload className="h-5 w-5" /></span>
            <p className="text-[15px] font-bold text-ink">Drop to upload</p>
            <p className="text-[12.5px] text-ink-3">Choose the project and type next — a matching title is saved as the next version.</p>
          </div>
        </div>
      ) : null}

      <UploadDocumentPanel
        open={upload.open}
        onOpenChange={(open) => setUpload((current) => ({ ...current, open }))}
        initial={upload.initial}
        dropped={upload.dropped}
        docs={pool}
        projectList={projectList}
        canSeeLpoLoi={canSeeLpoLoi}
        onUploaded={onUploaded}
      />

      <UploadVersionDialog
        open={Boolean(versionTarget)}
        onOpenChange={(open) => { if (!open) { setVersionTarget(null); setVersionFile(null); } }}
        document={versionTarget}
        nextVersion={nextVersionOf(versionTarget)}
        onUpload={uploadVersion}
        initialFile={versionFile}
      />

      <CompareVersionsDialog
        open={Boolean(compare)}
        onOpenChange={(open) => { if (!open) setCompare(null); }}
        older={compare?.older}
        newer={compare?.newer}
        onDownload={downloadDocument}
      />
    </div>
  );
}
